import React, { useState, useEffect, useRef } from 'react';
import { View, TouchableOpacity, ActivityIndicator, Animated, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { LinearGradient } from 'expo-linear-gradient';

import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { trialAPI } from '../../services/api';
import { trialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';
import { useTranslation } from '../../hooks/useTranslation';
import { trackAuthEvent } from '../../services/analytics';
import { fileExtensionOf } from '../../services/analyticsPrivacy';
import { brand } from '../../design-system/theme/tokens';

const COLORS = {
  primary: brand.primary,
  primaryLight: '#3B82F6',
  success: '#10B981',
  error: '#EF4444',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
  border: '#E5E7EB',
};

type UploadState = 'idle' | 'uploading' | 'analyzing' | 'complete' | 'error';

const ANALYZING_TIPS = [
  'Evaluating active listening markers...',
  'Analyzing powerful questioning techniques...',
  'Assessing coaching presence indicators...',
  'Reviewing direct communication patterns...',
  'Measuring awareness creation...',
];

interface TrialUploadCardProps {
  onComplete: (sessionId: number, taskId: string) => void;
  onSkip: () => void;
  language: string;
}

export default function TrialUploadCard({ onComplete, onSkip, language }: TrialUploadCardProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<UploadState>('idle');
  const [showHelp, setShowHelp] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tipIndex, setTipIndex] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;

  // Rotate tips during analysis
  useEffect(() => {
    if (state !== 'analyzing') return;
    const interval = setInterval(() => {
      Animated.timing(fadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }).start(() => {
        setTipIndex((prev) => (prev + 1) % ANALYZING_TIPS.length);
        Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }).start();
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [state]);

  // Pulse animation for upload icon
  useEffect(() => {
    if (state !== 'idle') return;
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(scaleAnim, { toValue: 1.05, duration: 1200, useNativeDriver: true }),
        Animated.timing(scaleAnim, { toValue: 1, duration: 1200, useNativeDriver: true }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [state]);

  const friendlyError = (err: any): string => {
    const trialUsed = trialAnalysisUsedMessage(t, language, err);
    if (trialUsed) return trialUsed;
    const detail = err?.response?.data?.detail || err?.message || '';
    const s = err?.response?.status;
    if (s === 413 || detail.includes('large')) return t('trial.upload.errorTooLarge') || 'File is too large. Try a shorter transcript.';
    if (s === 400) return t('trial.upload.errorInvalid') || 'This file type is not supported. Use PDF, Word, or text files.';
    if (s === 409) return t('trial.upload.errorDuplicate') || 'This transcript was already analyzed. Try a different one.';
    if (detail.includes('network') || detail.includes('fetch')) return t('trial.upload.errorNetwork') || 'Connection lost. Check your internet and try again.';
    return t('trial.upload.errorGeneric') || 'Something went wrong. Please try again.';
  };

  const handlePickFile = async () => {
    try {
      trackAuthEvent('first_upload_picker_opened', { surface: 'trial_upload_card' });
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
        ],
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets?.length) {
        trackAuthEvent('first_upload_cancelled', { surface: 'trial_upload_card' });
        return;
      }

      const file = result.assets[0];
      setFileName(file.name);
      setState('uploading');
      setErrorMessage(null);
      trackAuthEvent('first_upload_started', {
        surface: 'trial_upload_card',
        file_extension: fileExtensionOf(file.name),
        file_size: (file as any).size,
        mime_type: (file as any).mimeType,
      });

      try {
        setState('analyzing');
        const response = await trialAPI.startAnalysis(file, language);
        trackAuthEvent('first_upload_succeeded', {
          surface: 'trial_upload_card',
          session_id: response.session_id,
          task_id: response.task_id,
        });
        setState('complete');
        setTimeout(() => {
          onComplete(response.session_id, response.task_id);
        }, 800);
      } catch (err: any) {
        setState('error');
        setErrorMessage(friendlyError(err));
        trackAuthEvent('first_upload_failed', {
          surface: 'trial_upload_card',
          status: err?.response?.status,
          error_type: err?.name || 'unknown',
        });
      }
    } catch (err) {
      setState('error');
      setErrorMessage(friendlyError(err));
      trackAuthEvent('first_upload_failed', {
        surface: 'trial_upload_card',
        error_type: 'picker_error',
      });
    }
  };

  return (
    <View>
      <Card className="mb-4 overflow-hidden">
        <CardContent className="p-0">
          {/* Header gradient strip */}
          <LinearGradient
            colors={[COLORS.primary, COLORS.primaryLight]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ paddingVertical: 16, paddingHorizontal: 24 }}
          >
            <Text className="text-white text-center font-bold text-base">
              {t('trial.upload.headerBadge') || 'ICF feedback in minutes'}
            </Text>
          </LinearGradient>

          <View className="items-center px-6 pt-6 pb-5">
            {/* State-specific icon */}
            <Animated.View
              style={{ transform: [{ scale: state === 'idle' ? scaleAnim : 1 }] }}
            >
              <View
                className="w-16 h-16 rounded-2xl items-center justify-center mb-4"
                style={{
                  backgroundColor:
                    state === 'complete'
                      ? `${COLORS.success}15`
                      : state === 'error'
                        ? `${COLORS.error}15`
                        : `${COLORS.primary}10`,
                }}
              >
                {state === 'uploading' || state === 'analyzing' ? (
                  <ActivityIndicator size="large" color={COLORS.primary} />
                ) : state === 'complete' ? (
                  <MaterialIcons name="check-circle" size={32} color={COLORS.success} />
                ) : state === 'error' ? (
                  <MaterialIcons name="error-outline" size={32} color={COLORS.error} />
                ) : (
                  <MaterialIcons name="auto-awesome" size={32} color={COLORS.primary} />
                )}
              </View>
            </Animated.View>

            {/* Title */}
            <Text className="text-lg font-bold text-center mb-1" style={{ color: COLORS.text.primary }}>
              {state === 'idle' && (t('trial.upload.title') || 'See Your Coaching Level Instantly')}
              {state === 'uploading' && (t('trial.upload.uploading') || 'Uploading...')}
              {state === 'analyzing' && (t('trial.upload.analyzing') || 'Analyzing your transcript...')}
              {state === 'complete' && (t('trial.upload.complete') || 'Analysis complete!')}
              {state === 'error' && (t('trial.upload.error') || 'Something went wrong')}
            </Text>

            {/* Idle state description */}
            {state === 'idle' && (
              <Text className="text-sm text-center mb-5 leading-5" style={{ color: COLORS.text.secondary }}>
                {t('trial.upload.description') ||
                  'Upload one coaching transcript and get a full 8-competency ICF analysis with your ACC/PCC/MCC level.'}
              </Text>
            )}

            {/* Transcript help toggle */}
            {state === 'idle' && (
              <>
                <TouchableOpacity onPress={() => setShowHelp(!showHelp)} style={{ marginBottom: showHelp ? 0 : 12 }}>
                  <Text style={{ color: COLORS.primary, fontSize: 13 }}>
                    {showHelp
                      ? (t('trial.upload.helpToggle') || "What's a transcript? ▼").replace('▼', '▲')
                      : (t('trial.upload.helpToggle') || "What's a transcript? ▼")}
                  </Text>
                </TouchableOpacity>
                {showHelp && (
                  <View style={{ backgroundColor: '#F0F4FF', padding: 12, borderRadius: 8, marginTop: 8, marginBottom: 12, width: '100%' }}>
                    <Text style={{ color: COLORS.text.secondary, fontSize: 13, lineHeight: 20 }}>
                      {t('trial.upload.helpContent') || "A record of a coaching conversation between you and your client. For example:\n• A Zoom/Teams meeting transcript\n• A chat or email exchange\n• Notes you typed from a session"}
                    </Text>
                  </View>
                )}
              </>
            )}

            {/* Analyzing state with rotating tips */}
            {state === 'analyzing' && (
              <View className="items-center mb-4">
                <Animated.View style={{ opacity: fadeAnim }}>
                  <Text className="text-sm text-center italic" style={{ color: COLORS.primary }}>
                    {ANALYZING_TIPS[tipIndex]}
                  </Text>
                </Animated.View>
                <Text className="text-xs text-center mt-2" style={{ color: COLORS.text.muted }}>
                  {t('trial.upload.analyzingDescription') || 'Usually takes 60–90 seconds'}
                </Text>
                <TouchableOpacity onPress={onSkip} style={{ marginTop: 8 }}>
                  <Text style={{ color: COLORS.text.muted, fontSize: 13, textDecorationLine: 'underline', textAlign: 'center' }}>
                    {t('trial.upload.skipToPricing') || 'View plans instead'}
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {/* File name badge */}
            {fileName && state !== 'idle' && (
              <View className="flex-row items-center mb-3 px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#F3F4F6' }}>
                <MaterialIcons name="description" size={16} color={COLORS.text.muted} />
                <Text className="ml-2 text-sm" style={{ color: COLORS.text.secondary }} numberOfLines={1}>
                  {fileName}
                </Text>
              </View>
            )}

            {/* Error message */}
            {errorMessage && (
              <View className="mb-3 px-3 py-2 rounded-lg w-full" style={{ backgroundColor: `${COLORS.error}10` }}>
                <Text className="text-sm text-center" style={{ color: COLORS.error }}>
                  {errorMessage}
                </Text>
              </View>
            )}

            {/* CTA button */}
            {(state === 'idle' || state === 'error') && (
              <TouchableOpacity onPress={handlePickFile} className="w-full rounded-xl overflow-hidden" activeOpacity={0.85}>
                <LinearGradient
                  colors={[COLORS.primary, '#2563EB']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{ paddingVertical: 14, paddingHorizontal: 20, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
                >
                  <MaterialIcons name={state === 'error' ? 'refresh' : 'file-upload'} size={20} color="white" />
                  <Text className="text-white font-bold ml-2 text-base">
                    {state === 'error'
                      ? (t('trial.upload.tryAgain') || 'Try Again')
                      : (t('trial.upload.selectFile') || 'Upload Transcript')}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>
            )}

            {/* Complete state */}
            {state === 'complete' && (
              <View className="flex-row items-center">
                <MaterialIcons name="check" size={20} color={COLORS.success} />
                <Text className="ml-2 font-medium" style={{ color: COLORS.success }}>
                  {t('trial.upload.redirecting') || 'Opening your results...'}
                </Text>
              </View>
            )}

            {/* What you'll get section - idle and analyzing */}
            {(state === 'idle' || state === 'analyzing') && (
              <View className="w-full mt-4 pt-4" style={{ borderTopWidth: 1, borderTopColor: COLORS.border }}>
                <Text className="text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: COLORS.text.muted }}>
                  {t('trial.upload.whatYouGet') || "What you'll get"}
                </Text>
                {[
                  { icon: 'assessment' as const, text: t('trial.upload.feature1') || 'Full 8-competency ICF analysis' },
                  { icon: 'emoji-events' as const, text: t('trial.upload.feature2') || 'Your ACC / PCC / MCC level' },
                  { icon: 'chat' as const, text: t('trial.upload.feature3') || '20 AI mentor conversations about your results' },
                ].map((item, idx) => (
                  <View key={idx} className="flex-row items-center mb-2">
                    <MaterialIcons name={item.icon} size={16} color={COLORS.primary} />
                    <Text className="ml-2 text-sm" style={{ color: COLORS.text.secondary }}>{item.text}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Supported formats - idle only */}
            {state === 'idle' && (
              <View className="flex-row items-center mt-3" style={{ gap: 12 }}>
                {['PDF', 'DOCX', 'TXT'].map((fmt) => (
                  <View key={fmt} className="flex-row items-center px-2 py-1 rounded" style={{ backgroundColor: '#F3F4F6' }}>
                    <Text className="text-xs font-medium" style={{ color: COLORS.text.muted }}>{fmt}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </CardContent>
      </Card>

      <TouchableOpacity onPress={onSkip} className="py-2">
        <Text className="text-center text-sm underline" style={{ color: COLORS.text.muted }}>
          {t('trial.upload.skipToPricing') || 'View plans instead'}
        </Text>
      </TouchableOpacity>
    </View>
  );
}
